import { useState } from 'react';
import { declineRescue, rescueStreak, useCurrentStreak, useStreakShields } from '@busy/api';
import { ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';

// Rescue prompt — fires whenever the user opens the app and the streak
// row has broke_at set, meaning the bump trigger detected a gap that
// exhausted the cooloff bank. Two outcomes:
//
//   YES → rescueStreak(N) consumes N shields and restores the streak
//          (current_streak becomes pre_break_streak + 1).
//   NO  → asks for confirmation first (the second screen). If they
//          confirm, declineRescue() clears broke_at + pre_break_streak
//          so the modal doesn't reappear; shields stay banked. If they
//          back out of the confirmation, they're returned to the first
//          screen so they can change their mind.
//
// Both paths invalidate the 'streak' cache so the Today ribbon updates
// immediately. If the user has insufficient shields, the modal still
// renders but the YES button is disabled and an explainer ("you'd need
// X but only have Y") replaces the standard cost text — the No path
// (with confirmation) is then their only option.

const SHIELD_COVERAGE_DAYS = 7;

export function RescueStreakModal() {
  const streak = useCurrentStreak();
  const shields = useStreakShields();
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Two-step decline flow — first tap on No reveals a confirmation
  // screen ("are you sure? your X-day streak will be lost") so a
  // mis-tap doesn't cost a long streak. Second tap actually fires
  // declineRescue. Yes path skips the confirmation since saving is
  // the safe outcome.
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  if (!streak.data) return null;
  const broken = streak.data.broke_at;
  const preBreak = streak.data.pre_break_streak;
  if (!broken || !preBreak || preBreak <= 0) return null;

  const brokenAt = new Date(broken);
  const missedDays = Math.max(
    0,
    Math.floor((Date.now() - brokenAt.getTime()) / (24 * 60 * 60 * 1000)),
  );
  const shieldsNeeded = Math.max(1, Math.ceil(missedDays / SHIELD_COVERAGE_DAYS));
  const shieldsAvailable = shields.data?.length ?? 0;
  const canRescue = shieldsAvailable >= shieldsNeeded;

  async function handleYes() {
    if (!canRescue) return;
    setBusy(true);
    const { error } = await rescueStreak(shieldsNeeded);
    setBusy(false);
    if (error) {
      toast(t('rescueStreak.failed', { message: error.message }), 'error');
      return;
    }
    toast(t('rescueStreak.restored', { days: preBreak ?? 0 }), 'success');
  }

  function handleNoFirstTap() {
    setConfirmingDecline(true);
  }

  async function handleConfirmDecline() {
    setBusy(true);
    const { error } = await declineRescue();
    setBusy(false);
    if (error) {
      toast(t('rescueStreak.declineFailed', { message: error.message }), 'error');
      return;
    }
  }

  function handleBackOut() {
    setConfirmingDecline(false);
  }

  return (
    <ModalShell
      open
      onClose={() => undefined /* require explicit Y/N */}
      variant="card"
      size="md"
      ariaLabel={t('rescueStreak.aria')}
      dismissOnBackdrop={false}
    >
      <div className="bg-surface rounded-2xl">
        {confirmingDecline ? (
          // Second screen — "are you sure?" confirmation. Visually
          // distinct (red header) from the first screen so a thumb
          // doesn't blow through it.
          <>
            <div className="bg-red-600 text-white px-6 pt-6 pb-5 rounded-t-2xl">
              <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-white/80 mb-1">
                {t('rescueStreak.confirm.eyebrow')}
              </div>
              <h2 className="font-display text-2xl leading-tight">
                {t('rescueStreak.confirm.title', { days: preBreak })}
              </h2>
              <p className="text-white/80 text-[13px] leading-snug mt-2">
                {t('rescueStreak.confirm.body', { days: preBreak })}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={handleBackOut}
                  disabled={busy}
                  className="h-12 rounded-xl bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-[14px] disabled:opacity-50"
                >
                  {t('rescueStreak.confirm.takeMeBack')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDecline}
                  disabled={busy}
                  className="h-11 rounded-xl border-2 border-red-500/70 bg-surface text-red-600 dark:text-red-400 font-bold text-[13px] disabled:opacity-50"
                >
                  {busy ? t('rescueStreak.confirm.declining') : t('rescueStreak.confirm.yesImSure')}
                </button>
              </div>
            </div>
          </>
        ) : (
          // First screen — normal Y/N rescue prompt.
          <>
            <div className="bg-navy-900 text-white px-6 pt-6 pb-5 rounded-t-2xl">
              <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-400 mb-1">
                {t('rescueStreak.eyebrow')}
              </div>
              <h2 className="font-display text-2xl leading-tight">
                {t('rescueStreak.title', { days: preBreak })}
              </h2>
              <p className="text-white/72 text-[13px] leading-snug mt-2">
                {t('rescueStreak.body', { days: missedDays })}
              </p>
            </div>

            <div className="p-5 space-y-3">
              {canRescue ? (
                <div className="rounded-xl border border-gold-500/40 bg-gold-500/10 p-3">
                  <div className="text-[12px] text-ink-700 leading-snug">
                    {t('rescueStreak.cost', {
                      shields: shieldsNeeded,
                      banked: shieldsAvailable,
                    })}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-red-500/40 bg-red-50 dark:bg-red-600/10 p-3 text-[12px] text-red-900 dark:text-red-200 leading-snug">
                  {t('rescueStreak.insufficient', {
                    shields: shieldsNeeded,
                    banked: shieldsAvailable,
                  })}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleNoFirstTap}
                  disabled={busy}
                  className="h-12 rounded-xl border border-line bg-surface text-ink-700 font-bold text-[14px] disabled:opacity-50"
                >
                  {t('rescueStreak.no')}
                </button>
                <button
                  type="button"
                  onClick={handleYes}
                  disabled={busy || !canRescue}
                  className="h-12 rounded-xl bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-[14px] disabled:opacity-50"
                >
                  {busy ? t('rescueStreak.restoring') : t('rescueStreak.yes')}
                </button>
              </div>

              <p className="text-[11px] text-ink-500 leading-snug">
                {t('rescueStreak.footnote')}
              </p>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}
